import { useState } from "react";

/**
 * Industry-standard EIR damage codes.
 */
export const DAMAGE_CODES = [
  { code: "BR", label: "Broken" },
  { code: "BT", label: "Bent" },
  { code: "CR", label: "Crack" },
  { code: "CT", label: "Cut" },
  { code: "DH", label: "Hole" },
  { code: "DT", label: "Dent" },
  { code: "RU", label: "Rust" },
  { code: "MS", label: "Missing" },
  { code: "HO", label: "Holed" },
  { code: "BN", label: "Burn" },
  { code: "LO", label: "Loose" },
  { code: "SC", label: "Scratched" },
];

export const SIDES = [
  { id: "TOP", label: "Top (roof)" },
  { id: "BOTTOM", label: "Bottom (underside)" },
  { id: "LEFT", label: "Left side" },
  { id: "RIGHT", label: "Right side" },
  { id: "FRONT", label: "Front (door end)" },
  { id: "BACK", label: "Back (blank end)" },
  { id: "INTERIOR", label: "Interior" },
];

// A simple wireframe for each view. We use a long rectangle for side views
// (left/right/top/bottom) and a square for the ends (front/back).
function PanelRect({ side, width, height, label }) {
  // Side / top / bottom view: long rect with corner posts and door indicators
  if (["LEFT", "RIGHT", "TOP", "BOTTOM"].includes(side)) {
    return (
      <g>
        <rect x="0" y="0" width={width} height={height} fill="#fff" stroke="#003147" strokeWidth="2" />
        {/* Corrugation lines */}
        {Array.from({ length: 16 }).map((_, i) => (
          <line
            key={i}
            x1={(width / 16) * (i + 0.5)}
            y1="6"
            x2={(width / 16) * (i + 0.5)}
            y2={height - 6}
            stroke="#cbd5e1"
            strokeWidth="0.5"
          />
        ))}
        {/* Corner posts (left/right) */}
        <rect x="0" y="0" width="6" height={height} fill="#003147" />
        <rect x={width - 6} y="0" width="6" height={height} fill="#003147" />
        {/* Door indicator on FRONT side appears on the left for LEFT view */}
        {(side === "LEFT" || side === "RIGHT") && (
          <>
            <line x1={width - 14} y1="6" x2={width - 14} y2={height - 6} stroke="#003147" strokeWidth="1" strokeDasharray="3 3" />
            <text x={width - 28} y={height / 2 + 4} textAnchor="end" fontSize="9" fill="#64748b">door</text>
          </>
        )}
        <text x={6} y={-4} fontSize="10" fontWeight="700" fill="#003147" letterSpacing="0.5">{label}</text>
      </g>
    );
  }
  // End views (FRONT/BACK)
  return (
    <g>
      <rect x="0" y="0" width={width} height={height} fill="#fff" stroke="#003147" strokeWidth="2" />
      <rect x="0" y="0" width="6" height={height} fill="#003147" />
      <rect x={width - 6} y="0" width="6" height={height} fill="#003147" />
      <rect x="0" y="0" width={width} height="6" fill="#003147" />
      <rect x="0" y={height - 6} width={width} height="6" fill="#003147" />
      {side === "FRONT" && (
        <>
          {/* door split */}
          <line x1={width / 2} y1="8" x2={width / 2} y2={height - 8} stroke="#003147" strokeWidth="1" />
          {/* locking bars */}
          {[0.25, 0.5, 0.75].map((p) => (
            <line key={p} x1="8" y1={height * p} x2={width - 8} y2={height * p} stroke="#94a3b8" strokeWidth="0.8" />
          ))}
        </>
      )}
      <text x={6} y={-4} fontSize="10" fontWeight="700" fill="#003147" letterSpacing="0.5">{label}</text>
    </g>
  );
}

function DamageMarker({ damage, n, selected, onClick }) {
  const color = damage.severity === "CRITICAL" ? "#dc2626" : damage.severity === "MAJOR" ? "#ea580c" : "#99D420";
  return (
    <g transform={`translate(${damage.x}, ${damage.y})`} style={{ cursor: "pointer" }} onClick={onClick}>
      <circle r="9" fill={color} stroke={selected ? "#006E9E" : "#003147"} strokeWidth={selected ? 2.5 : 1.5} />
      <text textAnchor="middle" dy="3.5" fontSize="10" fontWeight="700" fill="#003147">{n}</text>
    </g>
  );
}

/**
 * Six-panel container diagram. Click anywhere on a panel to drop a damage marker.
 * Props:
 *  - damages: array of { code, side, x, y, description, severity }
 *  - onChange(damages): callback when array updates
 *  - readOnly: bool
 */
export default function ContainerDiagram({ damages = [], onChange, readOnly = false }) {
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [draftCode, setDraftCode] = useState("DT");
  const [draftSeverity, setDraftSeverity] = useState("MINOR");

  const longW = 320, longH = 70;     // top/bottom/left/right view dims
  const endW = 100, endH = 70;       // front/back

  // Layout positions for each panel (x offset, y offset, width, height, side)
  const panels = [
    { side: "TOP",    label: "TOP",    x: 20,  y: 30,  w: longW, h: longH },
    { side: "LEFT",   label: "LEFT",   x: 20,  y: 140, w: longW, h: longH },
    { side: "BOTTOM", label: "BOTTOM", x: 20,  y: 250, w: longW, h: longH },
    { side: "RIGHT",  label: "RIGHT",  x: 20,  y: 360, w: longW, h: longH },
    { side: "FRONT",  label: "FRONT",  x: 360, y: 30,  w: endW,  h: longH * 2 + 40 },
    { side: "BACK",   label: "BACK",   x: 360, y: 250, w: endW,  h: longH * 2 + 40 },
  ];

  const handlePanelClick = (e, panel) => {
    if (readOnly) return;
    const svgRect = e.currentTarget.getBoundingClientRect();
    const localX = ((e.clientX - svgRect.left) / svgRect.width) * 100;
    const localY = ((e.clientY - svgRect.top) / svgRect.height) * 100;
    const next = [...damages, {
      code: draftCode,
      side: panel.side,
      x: Math.max(2, Math.min(98, localX)),
      y: Math.max(2, Math.min(98, localY)),
      description: "",
      severity: draftSeverity,
    }];
    onChange(next);
    setSelectedIdx(next.length - 1);
  };

  const updateDamage = (idx, patch) => {
    const next = damages.map((d, i) => i === idx ? { ...d, ...patch } : d);
    onChange(next);
  };

  const removeDamage = (idx) => {
    onChange(damages.filter((_, i) => i !== idx));
    setSelectedIdx(null);
  };

  // Counter for stable global numbering across panels (order = damages array order)
  return (
    <div className="space-y-3" data-testid="container-diagram">
      {!readOnly && (
        <div className="flex items-end gap-3 bg-gray-50 border border-gray-200 p-3 text-xs" data-testid="diagram-toolbar">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Damage code (next click)</label>
            <select value={draftCode} onChange={(e) => setDraftCode(e.target.value)} className="ps-input font-mono h-8" data-testid="diagram-code">
              {DAMAGE_CODES.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Severity</label>
            <select value={draftSeverity} onChange={(e) => setDraftSeverity(e.target.value)} className="ps-input font-mono h-8" data-testid="diagram-severity">
              <option value="MINOR">Minor</option>
              <option value="MAJOR">Major</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>
          <div className="text-[11px] text-gray-500">
            Click on any panel below to drop a damage marker. Click an existing marker to edit it.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 border border-gray-200 bg-white p-3 overflow-auto" data-testid="diagram-svg-container">
          {/* Width 480 lets both columns fit comfortably */}
          {panels.map((p, idx) => {
            const localDmgs = damages
              .map((d, i) => ({ d, i }))
              .filter(({ d }) => d.side === p.side);
            // Stable counter — find each damage's global index for the label
            return (
              <div key={p.side} className="mb-3" data-testid={`panel-${p.side}`}>
                <svg
                  viewBox={`-2 -16 ${p.w + 8} ${p.h + 22}`}
                  width="100%"
                  preserveAspectRatio="xMidYMid meet"
                  style={{ display: idx >= 4 ? "none" : "block" }}
                  onClick={(e) => handlePanelClick(e, p)}
                >
                  <PanelRect side={p.side} width={p.w} height={p.h} label={p.label} />
                  {localDmgs.map(({ d, i }) => (
                    <DamageMarker
                      key={i}
                      damage={{ ...d, x: (d.x / 100) * p.w, y: (d.y / 100) * p.h }}
                      n={i + 1}
                      selected={selectedIdx === i}
                      onClick={(e) => { e.stopPropagation(); setSelectedIdx(i); }}
                    />
                  ))}
                </svg>
              </div>
            );
          })}
          {/* End views row */}
          <div className="grid grid-cols-2 gap-3 mt-2">
            {panels.slice(4).map((p) => {
              const localDmgs = damages.map((d, i) => ({ d, i })).filter(({ d }) => d.side === p.side);
              return (
                <div key={p.side} data-testid={`panel-${p.side}`}>
                  <svg
                    viewBox={`-2 -16 ${p.w + 8} ${p.h + 22}`}
                    width="100%"
                    preserveAspectRatio="xMidYMid meet"
                    onClick={(e) => handlePanelClick(e, p)}
                  >
                    <PanelRect side={p.side} width={p.w} height={p.h} label={p.label} />
                    {localDmgs.map(({ d, i }) => (
                      <DamageMarker
                        key={i}
                        damage={{ ...d, x: (d.x / 100) * p.w, y: (d.y / 100) * p.h }}
                        n={i + 1}
                        selected={selectedIdx === i}
                        onClick={(e) => { e.stopPropagation(); setSelectedIdx(i); }}
                      />
                    ))}
                  </svg>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 border border-gray-200 bg-white p-3" data-testid="damage-list">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-2">
            Damage list ({damages.length})
          </div>
          {damages.length === 0 ? (
            <div className="text-gray-400 text-sm py-4 text-center">
              {readOnly ? "No damages recorded." : "Click on any container panel to add a damage marker."}
            </div>
          ) : (
            <ul className="space-y-2 max-h-[460px] overflow-y-auto">
              {damages.map((d, i) => (
                <li
                  key={i}
                  className={`border p-2 text-xs cursor-pointer ${selectedIdx === i ? "border-[#006E9E] bg-blue-50" : "border-gray-200"}`}
                  onClick={() => setSelectedIdx(i)}
                  data-testid={`damage-row-${i}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono font-bold">
                      #{i + 1} · {d.code} · {d.side}
                      <span className={`ml-2 ps-pill ${
                        d.severity === "CRITICAL" ? "ps-pill-error" :
                        d.severity === "MAJOR" ? "ps-pill-warning" : "ps-pill-neutral"
                      }`}><span className="dot" />{d.severity || "MINOR"}</span>
                    </div>
                    {!readOnly && (
                      <button
                        className="text-red-500 hover:text-red-700 text-[11px] font-semibold"
                        onClick={(e) => { e.stopPropagation(); removeDamage(i); }}
                        data-testid={`remove-damage-${i}`}
                      >Remove</button>
                    )}
                  </div>
                  {(selectedIdx === i && !readOnly) ? (
                    <div className="mt-2 space-y-1">
                      <select
                        value={d.code}
                        onChange={(e) => updateDamage(i, { code: e.target.value })}
                        className="ps-input h-7 text-xs font-mono w-full"
                      >
                        {DAMAGE_CODES.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.label}</option>)}
                      </select>
                      <select
                        value={d.severity || "MINOR"}
                        onChange={(e) => updateDamage(i, { severity: e.target.value })}
                        className="ps-input h-7 text-xs font-mono w-full"
                      >
                        <option value="MINOR">Minor</option>
                        <option value="MAJOR">Major</option>
                        <option value="CRITICAL">Critical</option>
                      </select>
                      <input
                        placeholder="Description (optional)"
                        value={d.description || ""}
                        onChange={(e) => updateDamage(i, { description: e.target.value })}
                        className="ps-input h-7 text-xs w-full"
                      />
                    </div>
                  ) : d.description ? (
                    <div className="text-gray-600 mt-1">{d.description}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
